import { PencilSimpleIcon } from "@phosphor-icons/react/dist/csr/PencilSimple";
import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { ServiceTitanInvoiceLink } from "@/core/components/service-titan-invoice-link";
import { ServiceTitanJobLink } from "@/core/components/service-titan-job-link";
import { Alert, AlertDescription } from "@volca/ui/components/ui/alert";
import { Badge } from "@volca/ui/components/ui/badge";
import { Button } from "@volca/ui/components/ui/button";
import { SimpleSidebarHeader } from "@volca/ui/components/ui/simple-sidebar";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { formatMoney } from "@/core/utils/money";
import { COMMISSION_TRACE_KEY } from "@/icm/constants";
import { useGetIcmCellOverrides } from "@/icm/query";
import type { IcmCellOverride, IcmLineItem } from "@/icm/types";
import { formatTraceValue, humanizeKey } from "@/icm/utils";
import { EditCellModal } from "./edit-cell-modal";

interface IcmLineItemSidebarProps {
  lineItem: IcmLineItem;
  onClose: () => void;
  businessId?: string | null;
  statementId?: string;
}

interface TraceEntry {
  key: string;
  label: string;
  rawValue: unknown;
  displayValue: unknown;
  override: IcmCellOverride | null;
}

export const IcmLineItemSidebar = ({
  lineItem,
  onClose,
  businessId,
  statementId,
}: IcmLineItemSidebarProps) => {
  const { data: overridesData } = useGetIcmCellOverrides(
    businessId,
    statementId,
  );

  const overrideMap = useMemo(
    () =>
      new Map(
        (overridesData ?? [])
          .filter(
            (o) =>
              o.current && o.source_record_id === lineItem.source_record_id,
          )
          .map((o) => [o.column_name, o] as const),
      ),
    [overridesData, lineItem.source_record_id],
  );

  const [editingCell, setEditingCell] = useState<{
    columnName: string;
    currentValue: unknown;
    existingOverride: IcmCellOverride | null;
  } | null>(null);

  const payoutOverride = overrideMap.get(COMMISSION_TRACE_KEY) ?? null;
  const traceEntries = useMemo<TraceEntry[]>(
    () =>
      Object.entries(lineItem.calculation_trace)
        .filter(([key]) => key.toLowerCase() !== COMMISSION_TRACE_KEY)
        .map(([key, rawValue]) => {
          const override = overrideMap.get(key) ?? null;

          return {
            key,
            label: humanizeKey(key),
            rawValue,
            displayValue: override ? override.value : rawValue,
            override,
          };
        }),
    [lineItem.calculation_trace, overrideMap],
  );

  return (
    <Stack align="start" gap={0} className="h-full w-full">
      <SimpleSidebarHeader
        title={lineItem.payout_rule_name}
        description={lineItem.customer_name || undefined}
        onClose={onClose}
      />
      <Stack align="start" gap={6} className="w-full px-5 py-4">
        {lineItem.job_id ? (
          <ServiceTitanJobLink jobId={lineItem.job_id} />
        ) : (
          lineItem.invoice_id && (
            <ServiceTitanInvoiceLink invoiceId={lineItem.invoice_id} />
          )
        )}

        <Stack className="w-full" align="start" gap={4}>
          <Stack className="w-full" align="start" direction="row" gap={8}>
            {lineItem.job_completed_on && (
              <Stack align="start" gap={1} className="w-[50%]">
                <Text variant="label" tone="muted">
                  Completed
                </Text>
                <Text>
                  {dayjs(lineItem.job_completed_on).format("MMM DD, YYYY")}
                </Text>
              </Stack>
            )}
            <Stack align="start" gap={1} className="w-[50%]">
              <Text variant="label" tone="muted">
                Payout
              </Text>
              <Stack direction="row" gap={2} align="center" className="group">
                <Text>
                  {formatMoney(
                    String(payoutOverride?.value ?? lineItem.payout_amount),
                  )}
                </Text>
                {payoutOverride && (
                  <Badge variant="secondary" color="information" size="sm">
                    Edited
                  </Badge>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  className="pointer-events-none text-subtle opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100 focus-visible:pointer-events-auto focus-visible:opacity-100"
                  onClick={() =>
                    setEditingCell({
                      columnName: COMMISSION_TRACE_KEY,
                      currentValue: lineItem.payout_amount,
                      existingOverride: payoutOverride,
                    })
                  }
                  aria-label="Edit payout"
                >
                  <PencilSimpleIcon className="h-4 w-4" />
                </Button>
              </Stack>
            </Stack>
          </Stack>
          {lineItem.business_unit_name && (
            <Stack align="start" gap={1}>
              <Text variant="label" tone="muted">
                Business unit
              </Text>
              <Text>{lineItem.business_unit_name}</Text>
            </Stack>
          )}
        </Stack>
      </Stack>

      <div className="w-full flex-1 overflow-y-auto">
        <Stack align="start" gap={1} className="w-full px-5 py-2">
          {lineItem.warnings?.length > 0 && (
            <Stack align="start" gap={2} className="w-full pt-4 pb-2">
              {lineItem.warnings.map((msg, i) => (
                <Alert key={i} color="warning" role="status">
                  <WarningIcon />
                  <AlertDescription>
                    <Text>{msg}</Text>
                  </AlertDescription>
                </Alert>
              ))}
            </Stack>
          )}
          {traceEntries.map((entry) => (
            <Stack
              className="group w-full py-2"
              align="center"
              key={entry.key}
              gap={4}
              direction="row"
              justify="between"
            >
              <Stack direction="row" gap={2} align="center">
                <Text tone="muted">{entry.label}</Text>
                {entry.override && (
                  <Badge variant="secondary" color="information" size="sm">
                    Edited
                  </Badge>
                )}
              </Stack>
              <Stack direction="row" gap={2} align="center">
                {entry.override?.stale && (
                  <WarningIcon
                    className="h-4 w-4 text-warning"
                    role="img"
                    aria-label="Override may be stale"
                  >
                    <title>Override may be stale</title>
                  </WarningIcon>
                )}
                <Text>{formatTraceValue(entry.key, entry.displayValue)}</Text>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  className="pointer-events-none text-subtle opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100 focus-visible:pointer-events-auto focus-visible:opacity-100"
                  onClick={() =>
                    setEditingCell({
                      columnName: entry.key,
                      currentValue: entry.rawValue,
                      existingOverride: entry.override,
                    })
                  }
                  aria-label={`Edit ${entry.label}`}
                >
                  <PencilSimpleIcon className="h-4 w-4" />
                </Button>
              </Stack>
            </Stack>
          ))}
        </Stack>
      </div>

      <EditCellModal
        open={!!editingCell}
        onOpenChange={(open) => !open && setEditingCell(null)}
        businessId={businessId}
        statementId={statementId}
        lineItem={lineItem}
        columnName={editingCell?.columnName ?? ""}
        currentValue={editingCell?.currentValue}
        existingOverride={editingCell?.existingOverride ?? null}
      />
    </Stack>
  );
};
